import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const serviceDropdownItems = [
  { label: 'Water Damage', icon: 'ri-drop-line', path: '/services/water-damage' },
  { label: 'Fire Damage', icon: 'ri-fire-line', path: '/services/fire-damage' },
  { label: 'Mold Remediation', icon: 'ri-leaf-line', path: '/services/mold-remediation' },
  { label: 'Storm Damage', icon: 'ri-thunderstorms-line', path: '/services/storm-damage' },
  { label: 'Commercial', icon: 'ri-building-2-line', path: '/services/commercial' },
  { label: 'Reconstruction', icon: 'ri-tools-line', path: '/services/reconstruction' },
];

type NavItem =
  | { label: string; type: 'link'; path: string }
  | { label: string; type: 'scroll'; id: string };

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const servicesRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target as Node)) {
        setIsServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setIsServicesOpen(false);
    setIsMobileMenuOpen(false);
    setIsMobileServicesOpen(false);
  }, [location.pathname]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const navTextClass = (isScrolled || !isHome)
    ? 'text-gray-700 hover:text-teal-600'
    : 'text-white hover:text-teal-300';

  const navBgClass = (isScrolled || !isHome) ? 'bg-white shadow-md' : 'bg-transparent';

  const navItems: NavItem[] = [
    { label: 'Blog', type: 'link', path: '/blog' },
    { label: 'Cost Calculator', type: 'link', path: '/cost-calculator' },
    { label: 'About', type: 'link', path: '/about' },
    { label: 'Contact', type: 'link', path: '/contact' },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBgClass}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center">
            <Link to="/" className={`text-xl font-bold whitespace-nowrap ${(isScrolled || !isHome) ? 'text-teal-700' : 'text-white'}`}>
              Indoor-Restore Services
            </Link>
          </div>

          <div className="hidden lg:flex items-center space-x-7">
            {/* Home */}
            <Link
              to="/"
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${navTextClass}`}
            >
              Home
            </Link>

            {/* Services Dropdown — click to open/close */}
            <div ref={servicesRef} className="relative">
              <div className="flex items-center">
                <Link
                  to="/services"
                  onClick={() => {
                    setIsServicesOpen(false);
                    window.scrollTo({ top: 0, behavior: 'instant' });
                  }}
                  className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${navTextClass}`}
                >
                  Services
                </Link>
                <button
                  onClick={() => setIsServicesOpen((prev) => !prev)}
                  className={`ml-1 flex items-center justify-center w-5 h-5 cursor-pointer ${navTextClass}`}
                  aria-label="Toggle services menu"
                >
                  <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`}></i>
                </button>
              </div>

              {isServicesOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50">
                  <div className="py-1">
                    {serviceDropdownItems.map((item) => (
                      <Link
                        key={item.label}
                        to={item.path}
                        onClick={() => setIsServicesOpen(false)}
                        className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 transition-colors cursor-pointer"
                      >
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className={`${item.icon} text-teal-500`}></i>
                        </div>
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Remaining nav items */}
            {navItems.map((item) =>
              item.type === 'scroll' ? (
                <button
                  key={item.label}
                  onClick={() => {
                    if (isHome) scrollToSection(item.id);
                    else navigate(`/#${item.id}`);
                  }}
                  className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${navTextClass}`}
                >
                  {item.label}
                </button>
              ) : (
                <Link
                  key={item.label}
                  to={item.path}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                  className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${navTextClass}`}
                >
                  {item.label}
                </Link>
              )
            )}

            <a
              href="tel:2094512288"
              className="bg-teal-600 text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              (209) 451-2288
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 cursor-pointer"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${(isScrolled || !isHome) ? 'text-gray-700' : 'text-white'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full text-left text-gray-700 hover:text-teal-600 py-2 text-sm font-medium cursor-pointer"
            >
              Home
            </Link>

            {/* Mobile Services Accordion */}
            <div>
              <div className="flex items-center justify-between">
                <Link
                  to="/services"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    window.scrollTo({ top: 0, behavior: 'instant' });
                  }}
                  className="text-gray-700 hover:text-teal-600 py-2 text-sm font-medium cursor-pointer"
                >
                  Services
                </Link>
                <button
                  onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                  className="p-2 text-gray-700 hover:text-teal-600 cursor-pointer"
                >
                  <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isMobileServicesOpen ? 'rotate-180' : ''}`}></i>
                </button>
              </div>
              {isMobileServicesOpen && (
                <div className="pl-4 space-y-1 pb-1">
                  {serviceDropdownItems.map((item) => (
                    <Link
                      key={item.label}
                      to={item.path}
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        setIsMobileServicesOpen(false);
                      }}
                      className="flex items-center gap-2 w-full text-left text-gray-600 hover:text-teal-600 py-1.5 text-sm cursor-pointer"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`${item.icon} text-teal-500 text-xs`}></i>
                      </div>
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {navItems.map((item) =>
              item.type === 'scroll' ? (
                <button
                  key={item.label}
                  onClick={() => {
                    if (isHome) scrollToSection(item.id);
                    else navigate(`/#${item.id}`);
                    setIsMobileMenuOpen(false);
                  }}
                  className="block w-full text-left text-gray-700 hover:text-teal-600 py-2 text-sm font-medium cursor-pointer"
                >
                  {item.label}
                </button>
              ) : (
                <Link
                  key={item.label}
                  to={item.path}
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    window.scrollTo({ top: 0, behavior: 'instant' });
                  }}
                  className="block w-full text-left text-gray-700 hover:text-teal-600 py-2 text-sm font-medium cursor-pointer"
                >
                  {item.label}
                </Link>
              )
            )}

            <a
              href="tel:2094512288"
              className="block bg-teal-600 text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors text-center whitespace-nowrap cursor-pointer mt-2"
            >
              (209) 451-2288
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
